import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../contexts/AuthContext';
import { confirmDialog, errorAlert, successAlert } from '../../helpers/alerts';
import { formatDateBR } from '../../helpers/formatDate';
import {
  TIPO_ATENDIMENTO_LABEL,
  STATUS_CHECKLIST,
  STATUS_EDITAVEIS,
  STATUS_RETORNO,
  conferirQuantidades,
  faltasDeEstoque,
  fmtQtd,
  hojeISO,
  lerQtd,
} from '../../helpers/insumoChecklist';

// Um checklist de atendimento em loja, do rascunho ao finalizado.
//
//   rascunho / em separacao -> formulario (dados, materiais, ferramentas)
//   pronto para saida       -> tela de conferencia + CONFIRMAR SAIDA
//   em atendimento / aguardando devolucao -> retorno (usada / devolvida)
//   finalizado / cancelado  -> so leitura (historico)
//
// O estoque so muda em dois momentos, ambos no backend: na saida (baixa a
// retirada) e ao finalizar (volta a devolvida).

const COD_COMPUTADOR = 'computador_mini';
const OUTROS = '__outros__';

const linhaVazia = () => ({
  marcado: false, qtd_retirada: '', caixa: false, caixa_detalhe: '', observacao: '', modelo: '', data_retirada: '',
});
let seqOutro = 0;
const outroVazio = () => {
  seqOutro += 1;
  return { key: `o${seqOutro}`, nome: '', qtd_retirada: '', caixa: false, caixa_detalhe: '', observacao: '' };
};

function isoData(v) {
  return v ? String(v).slice(0, 10) : '';
}

function mensagemErro(err, padrao) {
  return err?.response?.data?.message || padrao;
}

function StatusPill({ status }) {
  const s = STATUS_CHECKLIST[status] || { label: status, pill: '' };
  return <span className={`status-pill ${s.pill}`}>{s.label}</span>;
}

function ResumoAtendimento({ checklist }) {
  return (
    <div className="insumo-ck__resumo">
      <div><span>Tipo</span><strong>{TIPO_ATENDIMENTO_LABEL[checklist.tipo_atendimento]}</strong></div>
      <div><span>Local</span><strong>{checklist.localidade || '—'}</strong></div>
      <div><span>Data</span><strong>{formatDateBR(checklist.data_atendimento)}</strong></div>
      <div><span>Responsável</span><strong>{checklist.responsavel || '—'}</strong></div>
      <div><span>Status</span><StatusPill status={checklist.status} /></div>
      {checklist.chamado_id ? (
        <div><span>Chamado</span><strong>#{checklist.chamado_id}</strong></div>
      ) : null}
    </div>
  );
}

function ListaFerramentas({ ferramentas }) {
  if (!ferramentas?.length) return <p className="cell-muted">Nenhuma ferramenta registrada.</p>;
  return (
    <ul className="insumo-ck__ferramentas-lista">
      {ferramentas.map((f) => (
        <li key={f.nome} className={f.marcada ? '' : 'insumo-ck__nao-marcada'}>
          <i className={`bi ${f.marcada ? 'bi-check-square-fill' : 'bi-square'}`} /> {f.nome}
        </li>
      ))}
    </ul>
  );
}

function DetalheItem({ it }) {
  const partes = [];
  if (it.caixa) partes.push(`Caixa${it.caixa_detalhe ? `: ${it.caixa_detalhe}` : ''}`);
  if (it.modelo) partes.push(`Modelo: ${it.modelo}`);
  if (it.data_retirada) partes.push(`Retirada em ${formatDateBR(it.data_retirada)}`);
  if (it.observacao) partes.push(it.observacao);
  if (!partes.length) return null;
  return <div className="insumo-ck__detalhe">{partes.join(' · ')}</div>;
}

export default function ChecklistAtendimento({ checklistId, chamadoInicial = '', catalogo, onVoltar, onMudou }) {
  const { user } = useAuth();
  const materiais = useMemo(() => catalogo?.materiais || [], [catalogo]);
  const estoquePorInsumo = useMemo(
    () => Object.fromEntries(materiais.map((m) => [m.id, Number(m.estoque_atual)])),
    [materiais]
  );

  const [checklist, setChecklist] = useState(null);
  const [carregando, setCarregando] = useState(!!checklistId);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [editandoConferido, setEditandoConferido] = useState(false);

  // --- estado do formulario de preparacao
  const [dados, setDados] = useState({
    tipo_atendimento: '', localidade: '', data_atendimento: hojeISO(), responsavel: user?.nome || '', observacoes: '',
  });
  const [linhas, setLinhas] = useState({});
  const [outrosMarcado, setOutrosMarcado] = useState(false);
  const [outros, setOutros] = useState([outroVazio()]);
  const [ferramentas, setFerramentas] = useState(
    () => (catalogo?.ferramentas || []).map((nome) => ({ nome, marcada: false }))
  );
  const [novaFerramenta, setNovaFerramenta] = useState('');

  // --- estado do retorno
  const [retorno, setRetorno] = useState({});
  const [obsRetorno, setObsRetorno] = useState('');

  // --- chamado vinculado (vale em qualquer etapa do checklist)
  const [chamados, setChamados] = useState([]);
  const [chamadoId, setChamadoId] = useState(checklistId ? '' : String(chamadoInicial || ''));
  const [buscaChamado, setBuscaChamado] = useState('');
  const [vinculando, setVinculando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/insumos/checklists/chamados-abertos`)
      .then((res) => {
        const lista = res.data.data || [];
        setChamados(lista);
        // Checklist novo aberto a partir de um chamado: ja sugere a loja dele.
        const origem = !checklistId && chamadoInicial ? lista.find((c) => String(c.id) === String(chamadoInicial)) : null;
        if (origem?.solicitante_filial) {
          setDados((d) => (d.localidade ? d : { ...d, localidade: origem.solicitante_filial }));
        }
      })
      .catch(() => setChamados([]));
  }, [checklistId, chamadoInicial]);

  const aplicarChecklist = useCallback((c) => {
    setChecklist(c);
    setDados({
      tipo_atendimento: c.tipo_atendimento,
      localidade: c.localidade || '',
      data_atendimento: isoData(c.data_atendimento),
      responsavel: c.responsavel || '',
      observacoes: c.observacoes || '',
    });
    const novas = {};
    const novosOutros = [];
    c.itens.forEach((it) => {
      const base = {
        qtd_retirada: it.qtd_retirada != null ? String(Number(it.qtd_retirada)) : '',
        caixa: !!it.caixa,
        caixa_detalhe: it.caixa_detalhe || '',
        observacao: it.observacao || '',
      };
      if (it.insumo_id) {
        novas[it.insumo_id] = {
          ...linhaVazia(), ...base, marcado: true, modelo: it.modelo || '', data_retirada: isoData(it.data_retirada),
        };
      } else {
        seqOutro += 1;
        novosOutros.push({ key: `o${seqOutro}`, nome: it.nome, ...base });
      }
    });
    setLinhas(novas);
    setOutrosMarcado(novosOutros.length > 0);
    setOutros(novosOutros.length ? novosOutros : [outroVazio()]);
    // Ferramentas do catalogo + as extras que foram gravadas no checklist
    const gravadas = new Map((c.ferramentas || []).map((f) => [f.nome, !!f.marcada]));
    const base = (catalogo?.ferramentas || []).map((nome) => ({ nome, marcada: !!gravadas.get(nome) }));
    const extras = (c.ferramentas || []).filter((f) => !(catalogo?.ferramentas || []).includes(f.nome));
    setFerramentas([...base, ...extras.map((f) => ({ nome: f.nome, marcada: !!f.marcada }))]);
    setRetorno(Object.fromEntries(c.itens.map((it) => [it.id, {
      qtd_usada: it.qtd_usada != null ? String(Number(it.qtd_usada)) : '',
      qtd_devolvida: it.qtd_devolvida != null ? String(Number(it.qtd_devolvida)) : '',
      observacao: it.observacao || '',
    }])));
    setObsRetorno(c.observacoes_retorno || '');
    setChamadoId(c.chamado_id ? String(c.chamado_id) : '');
    setEditandoConferido(false);
  }, [catalogo]);

  useEffect(() => {
    if (!checklistId) return;
    setCarregando(true);
    axios.get(`${API}/insumos/checklists/${checklistId}`)
      .then((res) => aplicarChecklist(res.data.data))
      .catch((err) => setErro(mensagemErro(err, 'Não foi possível carregar o checklist.')))
      .finally(() => setCarregando(false));
  }, [checklistId, aplicarChecklist]);

  const status = checklist?.status || 'rascunho';
  const modoEdicao = !checklist
    || (STATUS_EDITAVEIS.includes(status) && (status !== 'pronto_para_saida' || editandoConferido));
  const modoConferencia = status === 'pronto_para_saida' && !editandoConferido;
  const modoRetorno = STATUS_RETORNO.includes(status);

  // --- itens montados a partir do formulario
  const itensForm = useMemo(() => {
    const doCadastro = materiais
      .filter((m) => linhas[m.id]?.marcado)
      .map((m) => {
        const l = linhas[m.id];
        return {
          insumo_id: m.id,
          nome: m.descricao,
          qtd_retirada: l.qtd_retirada,
          caixa: l.caixa,
          caixa_detalhe: l.caixa_detalhe,
          observacao: l.observacao,
          modelo: m.codigo === COD_COMPUTADOR ? l.modelo : '',
          data_retirada: m.codigo === COD_COMPUTADOR ? l.data_retirada : '',
        };
      });
    const livres = outrosMarcado
      ? outros.filter((o) => o.nome.trim() || o.qtd_retirada).map((o) => ({
        insumo_id: null, nome: o.nome.trim(), qtd_retirada: o.qtd_retirada, caixa: o.caixa,
        caixa_detalhe: o.caixa_detalhe, observacao: o.observacao,
      }))
      : [];
    return [...doCadastro, ...livres];
  }, [materiais, linhas, outrosMarcado, outros]);

  const faltas = useMemo(() => faltasDeEstoque(itensForm, estoquePorInsumo), [itensForm, estoquePorInsumo]);
  const faltaPorInsumo = useMemo(() => Object.fromEntries(faltas.map((f) => [f.insumo_id, f])), [faltas]);

  const pendenciasPreparo = useMemo(() => {
    const p = [];
    if (!dados.tipo_atendimento) p.push('Selecione Manutenção ou Instalação.');
    if (!dados.localidade.trim()) p.push('Informe a localidade / loja.');
    if (!dados.data_atendimento) p.push('Informe a data do atendimento.');
    if (!dados.responsavel.trim()) p.push('Informe o responsável / técnico.');
    if (!itensForm.length) p.push('Selecione pelo menos um material.');
    itensForm.forEach((it) => {
      const q = lerQtd(it.qtd_retirada);
      if (!it.nome) p.push('Item "Outros": informe o nome do material.');
      else if (Number.isNaN(q)) p.push(`${it.nome}: quantidade inválida.`);
      else if (!q) p.push(`${it.nome}: informe a quantidade a retirar.`);
    });
    return p;
  }, [dados, itensForm]);

  // --- acoes do formulario
  const setDado = (k, v) => setDados((d) => ({ ...d, [k]: v }));
  const setLinha = (id, patch) => setLinhas((ls) => ({ ...ls, [id]: { ...(ls[id] || linhaVazia()), ...patch } }));
  const setOutro = (key, patch) => setOutros((os) => os.map((o) => (o.key === key ? { ...o, ...patch } : o)));

  const aplicarSugestoes = (tipo) => {
    const sug = catalogo?.sugestoes?.[tipo];
    if (!sug) return;
    setLinhas((ls) => {
      const novo = { ...ls };
      materiais.filter((m) => sug.materiais.includes(m.codigo)).forEach((m) => {
        novo[m.id] = { ...(novo[m.id] || linhaVazia()), marcado: true };
      });
      return novo;
    });
    setFerramentas((fs) => fs.map((f) => (sug.ferramentas.includes(f.nome) ? { ...f, marcada: true } : f)));
  };

  const escolherTipo = (tipo) => {
    setDado('tipo_atendimento', tipo);
    // Checklist novo e ainda sem nada marcado: ja traz as sugestoes do tipo.
    const nadaMarcado = !Object.values(linhas).some((l) => l.marcado) && !ferramentas.some((f) => f.marcada);
    if (!checklist && nadaMarcado) aplicarSugestoes(tipo);
  };

  const adicionarFerramenta = () => {
    const nome = novaFerramenta.trim();
    if (!nome) return;
    if (!ferramentas.some((f) => f.nome.toLowerCase() === nome.toLowerCase())) {
      setFerramentas((fs) => [...fs, { nome, marcada: true }]);
    }
    setNovaFerramenta('');
  };

  const payloadPreparo = () => ({
    ...dados,
    itens: itensForm.map((it) => ({ ...it, qtd_retirada: lerQtd(it.qtd_retirada) ?? 0 })),
    ferramentas,
    // So vale na criacao; depois o vinculo muda pelo endpoint proprio.
    chamado_id: chamadoId ? Number(chamadoId) : null,
  });

  // Checklist ainda nao salvo: guarda a escolha e manda junto ao criar.
  // Ja salvo: grava o vinculo na hora (e o backend registra no chamado).
  const mudarChamado = async (valor) => {
    if (!checklist) { setChamadoId(valor); return; }
    if (!valor && checklist.chamado_id) {
      const ok = await confirmDialog({
        title: 'Remover o vínculo?',
        text: `O chamado #${checklist.chamado_id} recebe um registro de que este checklist foi desvinculado.`,
        confirmButtonText: 'Remover vínculo',
      });
      if (!ok.isConfirmed) return;
    }
    setVinculando(true);
    setErro('');
    try {
      const res = await axios.put(`${API}/insumos/checklists/${checklist.id}/chamado`, {
        chamado_id: valor ? Number(valor) : null,
      });
      aplicarChecklist(res.data.data);
      onMudou?.();
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao vincular o chamado.'));
    } finally {
      setVinculando(false);
    }
  };

  const salvar = async () => {
    const payload = payloadPreparo();
    const res = checklist
      ? await axios.put(`${API}/insumos/checklists/${checklist.id}`, payload)
      : await axios.post(`${API}/insumos/checklists`, payload);
    return res.data.data;
  };

  const salvarRascunho = async () => {
    if (!dados.tipo_atendimento) {
      setErro('Selecione Manutenção ou Instalação.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      aplicarChecklist(await salvar());
      onMudou?.();
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao salvar o checklist.'));
    } finally {
      setSalvando(false);
    }
  };

  const conferir = async () => {
    if (pendenciasPreparo.length || faltas.length) return;
    setSalvando(true);
    setErro('');
    try {
      const salvo = await salvar();
      aplicarChecklist(salvo);
      const res = await axios.post(`${API}/insumos/checklists/${salvo.id}/preparar`);
      aplicarChecklist(res.data.data);
      onMudou?.();
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao conferir o checklist.'));
    } finally {
      setSalvando(false);
    }
  };

  const confirmarSaida = async () => {
    const ok = await confirmDialog({
      title: 'Confirmar saída?',
      text: 'As quantidades retiradas serão baixadas do estoque agora. O que voltar sem uso retorna ao estoque ao finalizar.',
      confirmButtonText: 'Confirmar saída',
    });
    if (!ok.isConfirmed) return;
    setSalvando(true);
    setErro('');
    try {
      const res = await axios.post(`${API}/insumos/checklists/${checklist.id}/saida`);
      aplicarChecklist(res.data.data);
      onMudou?.();
      successAlert({ title: 'Saída registrada', text: 'Bom atendimento! Na volta, informe o que foi usado e devolvido.' });
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao confirmar a saída.'));
    } finally {
      setSalvando(false);
    }
  };

  const cancelar = async () => {
    const ok = await confirmDialog({
      title: 'Cancelar checklist?',
      text: 'Nada saiu do estoque ainda. O checklist fica registrado como cancelado.',
      confirmButtonText: 'Cancelar checklist',
      cancelButtonText: 'Voltar',
    });
    if (!ok.isConfirmed) return;
    setSalvando(true);
    try {
      const res = await axios.post(`${API}/insumos/checklists/${checklist.id}/cancelar`);
      aplicarChecklist(res.data.data);
      onMudou?.();
    } catch (err) {
      errorAlert({ title: 'Erro', text: mensagemErro(err, 'Falha ao cancelar.') });
    } finally {
      setSalvando(false);
    }
  };

  // --- retorno
  const conferencias = useMemo(() => {
    if (!checklist) return [];
    return checklist.itens.map((it) => ({
      it,
      conf: conferirQuantidades({
        nome: it.nome,
        qtd_retirada: it.qtd_retirada,
        qtd_usada: retorno[it.id]?.qtd_usada,
        qtd_devolvida: retorno[it.id]?.qtd_devolvida,
      }),
    }));
  }, [checklist, retorno]);
  const tudoConfere = conferencias.length > 0 && conferencias.every((c) => c.conf.ok);
  const divergencias = conferencias.filter((c) => !c.conf.ok && !c.conf.pendente);
  const incompletos = conferencias.filter((c) => c.conf.pendente);

  const setRet = (id, patch) => setRetorno((r) => ({ ...r, [id]: { ...r[id], ...patch } }));

  const payloadRetorno = () => ({
    observacoes_retorno: obsRetorno,
    itens: checklist.itens.map((it) => ({
      id: it.id,
      qtd_usada: lerQtd(retorno[it.id]?.qtd_usada),
      qtd_devolvida: lerQtd(retorno[it.id]?.qtd_devolvida),
      observacao: retorno[it.id]?.observacao || '',
    })),
  });

  const salvarRetorno = async () => {
    setSalvando(true);
    setErro('');
    try {
      const res = await axios.put(`${API}/insumos/checklists/${checklist.id}/retorno`, payloadRetorno());
      aplicarChecklist(res.data.data);
      onMudou?.();
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao salvar o retorno.'));
    } finally {
      setSalvando(false);
    }
  };

  const finalizar = async () => {
    if (!tudoConfere) return;
    const resumo = checklist.itens
      .map((it) => `${it.nome}: usou ${fmtQtd(lerQtd(retorno[it.id]?.qtd_usada))}, devolve ${fmtQtd(lerQtd(retorno[it.id]?.qtd_devolvida))}`)
      .join('; ');
    const ok = await confirmDialog({
      title: 'Finalizar atendimento?',
      text: `O que foi devolvido volta ao estoque. ${resumo}.`,
      confirmButtonText: 'Finalizar',
    });
    if (!ok.isConfirmed) return;
    setSalvando(true);
    setErro('');
    try {
      const res = await axios.post(`${API}/insumos/checklists/${checklist.id}/finalizar`, payloadRetorno());
      aplicarChecklist(res.data.data);
      onMudou?.();
      successAlert({ title: 'Atendimento finalizado', text: 'Estoque atualizado e histórico registrado.' });
    } catch (err) {
      setErro(mensagemErro(err, 'Falha ao finalizar.'));
    } finally {
      setSalvando(false);
    }
  };

  // --- render
  if (carregando) {
    return (
      <section className="content-card content-card--centered">
        <div className="screen-loader__spinner" />
        <p>Carregando checklist...</p>
      </section>
    );
  }

  const titulo = checklist ? `Checklist #${checklist.id}` : 'Novo checklist';

  // Chamados em aberto + o ja vinculado (que pode ter sido encerrado depois).
  const termoChamado = buscaChamado.trim().toLowerCase().replace(/^#/, '');
  const opcoesChamado = chamados.filter((c) => !termoChamado
    || String(c.id).includes(termoChamado)
    || [c.titulo, c.solicitante, c.solicitante_filial].filter(Boolean).join(' ').toLowerCase().includes(termoChamado)
    || String(c.id) === chamadoId);
  const vinculadoForaDaLista = checklist?.chamado_id && !chamados.some((c) => c.id === checklist.chamado_id);

  const blocoChamado = (
    <section className="content-card">
      <h3 className="insumo-ck__secao">Chamado vinculado</h3>
      <div className="insumo-ck__chamado">
        <input
          className="insumo-ck__texto" placeholder="Buscar nº ou título..."
          value={buscaChamado} onChange={(e) => setBuscaChamado(e.target.value)}
        />
        <select value={chamadoId} disabled={vinculando} onChange={(e) => mudarChamado(e.target.value)}>
          <option value="">Nenhum chamado</option>
          {vinculadoForaDaLista ? (
            <option value={String(checklist.chamado_id)}>
              #{checklist.chamado_id} — {checklist.chamado_titulo || 'chamado'} ({checklist.chamado_status})
            </option>
          ) : null}
          {opcoesChamado.map((c) => (
            <option key={c.id} value={String(c.id)}>
              #{c.id} — {c.titulo}{c.solicitante_filial ? ` · ${c.solicitante_filial}` : ''}
            </option>
          ))}
        </select>
      </div>
      <p className="content-card__hint" style={{ marginBottom: 0 }}>
        {chamadoId
          ? (checklist
            ? `Vinculado ao chamado #${chamadoId}. Saída, materiais usados/devolvidos e finalização ficam registrados nos comentários do chamado.`
            : `Ao salvar, o checklist é vinculado ao chamado #${chamadoId} e passa a registrar tudo nos comentários dele.`)
          : 'Opcional. Vincule a um chamado em aberto para que tudo o que for feito neste checklist fique registrado dentro dele.'}
      </p>
    </section>
  );

  const banner = (
    <>
      {erro ? (
        <div className="feedback-banner feedback-banner--error" style={{ whiteSpace: 'pre-line' }}>{erro}</div>
      ) : null}
      {blocoChamado}
    </>
  );

  const cabecalho = (
    <div className="insumo-ck__topo">
      <button type="button" className="btn-portal btn-portal--outline" onClick={onVoltar}>
        <i className="bi bi-arrow-left" /> Voltar
      </button>
      <h2>{titulo}</h2>
      {checklist ? <StatusPill status={checklist.status} /> : null}
    </div>
  );

  // ===================== CONFERENCIA =====================
  if (modoConferencia) {
    return (
      <div className="insumo-ck">
        {cabecalho}
        {banner}
        <section className="content-card">
          <h3 className="insumo-ck__secao">Atendimento</h3>
          <ResumoAtendimento checklist={checklist} />
          {checklist.observacoes ? <p className="insumo-ck__obs">{checklist.observacoes}</p> : null}
        </section>

        <section className="content-card">
          <h3 className="insumo-ck__secao">Materiais</h3>
          <div className="table-shell">
            <table className="portal-table">
              <thead><tr><th>Material</th><th>Retirada</th><th>Estoque</th></tr></thead>
              <tbody>
                {checklist.itens.map((it) => (
                  <tr key={it.id}>
                    <td><span className="cell-strong">{it.nome}</span><DetalheItem it={it} /></td>
                    <td>{fmtQtd(it.qtd_retirada)}</td>
                    <td className="cell-muted">{it.insumo_id ? fmtQtd(it.estoque_atual) : 'sem estoque'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="content-card">
          <h3 className="insumo-ck__secao">Ferramentas</h3>
          <ListaFerramentas ferramentas={checklist.ferramentas} />
        </section>

        <div className="portal-form-actions insumo-ck__acoes">
          <button type="button" className="btn-portal btn-portal--outline" disabled={salvando} onClick={cancelar}>
            <i className="bi bi-x-circle" /> Cancelar checklist
          </button>
          <button type="button" className="btn-portal btn-portal--outline" disabled={salvando} onClick={() => setEditandoConferido(true)}>
            <i className="bi bi-pencil" /> Voltar a editar
          </button>
          <button type="button" className="btn-portal btn-portal--primary" disabled={salvando} onClick={confirmarSaida}>
            <i className="bi bi-box-arrow-right" /> {salvando ? 'Registrando...' : 'Confirmar saída'}
          </button>
        </div>
      </div>
    );
  }

  // ===================== RETORNO =====================
  if (modoRetorno) {
    return (
      <div className="insumo-ck">
        {cabecalho}
        {banner}
        <section className="content-card">
          <ResumoAtendimento checklist={checklist} />
          {checklist.saida_em ? (
            <p className="cell-muted" style={{ marginTop: 10 }}>
              Saída em {new Date(checklist.saida_em).toLocaleString('pt-BR')} por {checklist.saida_por || '—'}
            </p>
          ) : null}
        </section>

        <section className="content-card">
          <h3 className="insumo-ck__secao">Retorno — o que foi usado e o que voltou</h3>
          <p className="content-card__hint">Retirada = usada + devolvida. O que for devolvido volta ao estoque ao finalizar.</p>
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Material</th><th>Retirada</th><th>Usada</th><th>Devolvida</th><th>Conferência</th><th>Observação</th>
                </tr>
              </thead>
              <tbody>
                {conferencias.map(({ it, conf }) => (
                  <tr key={it.id}>
                    <td><span className="cell-strong">{it.nome}</span><DetalheItem it={{ ...it, observacao: '' }} /></td>
                    <td>{fmtQtd(it.qtd_retirada)}</td>
                    <td>
                      <input
                        className="insumo-ck__qtd" inputMode="decimal"
                        value={retorno[it.id]?.qtd_usada ?? ''}
                        onChange={(e) => setRet(it.id, { qtd_usada: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="insumo-ck__qtd" inputMode="decimal"
                        value={retorno[it.id]?.qtd_devolvida ?? ''}
                        onChange={(e) => setRet(it.id, { qtd_devolvida: e.target.value })}
                      />
                    </td>
                    <td>
                      {conf.ok ? (
                        <span className="insumo-ck__ok"><i className="bi bi-check-circle-fill" /> Confere</span>
                      ) : conf.pendente ? (
                        <span className="cell-muted">Preencher</span>
                      ) : (
                        <span className="insumo-ck__alerta"><i className="bi bi-exclamation-triangle-fill" /> Não confere</span>
                      )}
                    </td>
                    <td>
                      <input
                        className="insumo-ck__texto"
                        value={retorno[it.id]?.observacao ?? ''}
                        onChange={(e) => setRet(it.id, { observacao: e.target.value })}
                        placeholder="Divergências, defeitos..."
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {tudoConfere ? (
            <div className="feedback-banner feedback-banner--info" style={{ marginTop: 14 }}>
              ✅ Quantidades conferem.
            </div>
          ) : divergencias.length ? (
            <div className="feedback-banner feedback-banner--warning" style={{ marginTop: 14 }}>
              <strong>⚠️ ATENÇÃO: as quantidades não conferem.</strong>
              <ul style={{ margin: '6px 0' }}>
                {divergencias.map(({ it, conf }) => <li key={it.id}>{conf.mensagem}</li>)}
              </ul>
              Verifique os valores antes de finalizar o atendimento.
            </div>
          ) : incompletos.length ? (
            <div className="feedback-banner feedback-banner--info" style={{ marginTop: 14 }}>
              Informe a quantidade usada e a devolvida de todos os materiais para finalizar.
            </div>
          ) : null}

          <label className="portal-field" style={{ marginTop: 14 }}>
            <span>Observações do retorno</span>
            <textarea rows={2} value={obsRetorno} onChange={(e) => setObsRetorno(e.target.value)} />
          </label>
        </section>

        <section className="content-card">
          <h3 className="insumo-ck__secao">Ferramentas levadas</h3>
          <ListaFerramentas ferramentas={checklist.ferramentas} />
        </section>

        <div className="portal-form-actions insumo-ck__acoes">
          <button type="button" className="btn-portal btn-portal--outline" disabled={salvando} onClick={salvarRetorno}>
            <i className="bi bi-save" /> Salvar retorno
          </button>
          <button
            type="button" className="btn-portal btn-portal--primary"
            disabled={salvando || !tudoConfere} onClick={finalizar}
            title={tudoConfere ? '' : 'As quantidades precisam conferir para finalizar'}
          >
            <i className="bi bi-check2-all" /> {salvando ? 'Finalizando...' : 'Finalizar atendimento'}
          </button>
        </div>
      </div>
    );
  }

  // ===================== SO LEITURA (finalizado / cancelado) =====================
  if (checklist && !modoEdicao) {
    return (
      <div className="insumo-ck">
        {cabecalho}
        {banner}
        <section className="content-card">
          <ResumoAtendimento checklist={checklist} />
          <p className="cell-muted" style={{ marginTop: 10 }}>
            {checklist.saida_em ? `Saída: ${new Date(checklist.saida_em).toLocaleString('pt-BR')} (${checklist.saida_por || '—'}). ` : ''}
            {checklist.finalizado_em ? `Finalizado: ${new Date(checklist.finalizado_em).toLocaleString('pt-BR')} (${checklist.finalizado_por || '—'}).` : ''}
            {checklist.cancelado_em ? `Cancelado: ${new Date(checklist.cancelado_em).toLocaleString('pt-BR')} (${checklist.cancelado_por || '—'}).` : ''}
          </p>
          {checklist.observacoes ? <p className="insumo-ck__obs">{checklist.observacoes}</p> : null}
          {checklist.observacoes_retorno ? <p className="insumo-ck__obs">Retorno: {checklist.observacoes_retorno}</p> : null}
        </section>
        <section className="content-card">
          <h3 className="insumo-ck__secao">Materiais</h3>
          <TabelaConsumo itens={checklist.itens} mostrarEstoque />
        </section>
        <section className="content-card">
          <h3 className="insumo-ck__secao">Ferramentas</h3>
          <ListaFerramentas ferramentas={checklist.ferramentas} />
        </section>
      </div>
    );
  }

  // ===================== PREPARACAO (formulario) =====================
  const ferramentasNaoMarcadas = ferramentas.filter((f) => !f.marcada);
  const podeConferir = !pendenciasPreparo.length && !faltas.length;

  return (
    <div className="insumo-ck">
      {cabecalho}
      {banner}

      <section className="content-card">
        <h3 className="insumo-ck__secao">Atendimento</h3>
        <div className="insumo-ck__tipos">
          {['manutencao', 'instalacao'].map((t) => (
            <label key={t} className={`insumo-ck__tipo ${dados.tipo_atendimento === t ? 'insumo-ck__tipo--on' : ''}`}>
              <input
                type="radio" name="tipo_atendimento" value={t}
                checked={dados.tipo_atendimento === t}
                onChange={() => escolherTipo(t)}
              />
              <i className={`bi ${t === 'manutencao' ? 'bi-tools' : 'bi-plug'}`} /> {TIPO_ATENDIMENTO_LABEL[t].toUpperCase()}
            </label>
          ))}
        </div>
        <div className="portal-form-grid" style={{ marginTop: 16 }}>
          <label className="portal-field">
            <span>Localidade / Loja</span>
            <input value={dados.localidade} maxLength={150} onChange={(e) => setDado('localidade', e.target.value)} placeholder="Ex.: Loja AC" />
          </label>
          <label className="portal-field">
            <span>Data do atendimento</span>
            <input type="date" value={dados.data_atendimento} onChange={(e) => setDado('data_atendimento', e.target.value)} />
          </label>
          <label className="portal-field">
            <span>Responsável / Técnico</span>
            <input value={dados.responsavel} maxLength={150} onChange={(e) => setDado('responsavel', e.target.value)} />
          </label>
          <label className="portal-field">
            <span>Observações gerais</span>
            <input value={dados.observacoes} onChange={(e) => setDado('observacoes', e.target.value)} />
          </label>
        </div>
      </section>

      <section className="content-card">
        <div className="insumo-ck__secao-topo">
          <h3 className="insumo-ck__secao">Materiais</h3>
          {dados.tipo_atendimento ? (
            <button type="button" className="btn-portal btn-portal--outline" onClick={() => aplicarSugestoes(dados.tipo_atendimento)}>
              <i className="bi bi-magic" /> Marcar sugeridos para {TIPO_ATENDIMENTO_LABEL[dados.tipo_atendimento]}
            </button>
          ) : null}
        </div>
        <p className="content-card__hint">
          Quantidades sempre em unidades. Usada e devolvida são informadas na volta do atendimento.
        </p>
        <div className="table-shell">
          <table className="portal-table insumo-ck__tabela">
            <thead>
              <tr><th /><th>Material</th><th>Estoque</th><th>Retirada</th><th>Caixa</th><th>Observação</th></tr>
            </thead>
            <tbody>
              {linhasMateriais(materiais).map((m) => {
                if (m === OUTROS) {
                  return (
                    <React.Fragment key={OUTROS}>
                      <tr className={outrosMarcado ? 'insumo-ck__linha--on' : ''}>
                        <td><input type="checkbox" checked={outrosMarcado} onChange={(e) => setOutrosMarcado(e.target.checked)} /></td>
                        <td><span className="cell-strong">OUTROS</span></td>
                        <td className="cell-muted">—</td>
                        <td colSpan={3} className="cell-muted">Materiais fora do cadastro (não mexem no estoque).</td>
                      </tr>
                      {outrosMarcado ? outros.map((o) => (
                        <tr key={o.key} className="insumo-ck__sub">
                          <td>
                            <button
                              type="button" className="insumo-ck__icone" title="Remover"
                              onClick={() => setOutros((os) => (os.length > 1 ? os.filter((x) => x.key !== o.key) : [outroVazio()]))}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </td>
                          <td>
                            <input className="insumo-ck__texto" placeholder="Nome do material" value={o.nome} maxLength={150}
                              onChange={(e) => setOutro(o.key, { nome: e.target.value })} />
                          </td>
                          <td className="cell-muted">—</td>
                          <td>
                            <input className="insumo-ck__qtd" inputMode="decimal" value={o.qtd_retirada}
                              onChange={(e) => setOutro(o.key, { qtd_retirada: e.target.value })} />
                          </td>
                          <td>
                            <input type="checkbox" checked={o.caixa} onChange={(e) => setOutro(o.key, { caixa: e.target.checked })} />
                            {o.caixa ? (
                              <input className="insumo-ck__texto" placeholder="Ex.: caixa com 100 un." value={o.caixa_detalhe}
                                onChange={(e) => setOutro(o.key, { caixa_detalhe: e.target.value })} />
                            ) : null}
                          </td>
                          <td>
                            <input className="insumo-ck__texto" value={o.observacao}
                              onChange={(e) => setOutro(o.key, { observacao: e.target.value })} />
                          </td>
                        </tr>
                      )) : null}
                      {outrosMarcado ? (
                        <tr className="insumo-ck__sub">
                          <td />
                          <td colSpan={5}>
                            <button type="button" className="btn-portal btn-portal--outline" onClick={() => setOutros((os) => [...os, outroVazio()])}>
                              <i className="bi bi-plus-lg" /> Outro material
                            </button>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                }
                const l = linhas[m.id] || linhaVazia();
                const falta = faltaPorInsumo[m.id];
                const ehComputador = m.codigo === COD_COMPUTADOR;
                return (
                  <React.Fragment key={m.id}>
                    <tr className={l.marcado ? 'insumo-ck__linha--on' : ''}>
                      <td><input type="checkbox" checked={l.marcado} onChange={(e) => setLinha(m.id, { marcado: e.target.checked })} /></td>
                      <td><span className="cell-strong">{m.descricao}</span></td>
                      <td className={Number(m.estoque_atual) > 0 ? '' : 'insumo-ck__alerta'}>{fmtQtd(m.estoque_atual)}</td>
                      <td>
                        <input
                          className="insumo-ck__qtd" inputMode="decimal" disabled={!l.marcado}
                          value={l.qtd_retirada}
                          onChange={(e) => setLinha(m.id, { qtd_retirada: e.target.value })}
                        />
                      </td>
                      <td>
                        <input type="checkbox" disabled={!l.marcado} checked={l.caixa} onChange={(e) => setLinha(m.id, { caixa: e.target.checked })} />
                      </td>
                      <td>
                        <input className="insumo-ck__texto" disabled={!l.marcado} value={l.observacao}
                          onChange={(e) => setLinha(m.id, { observacao: e.target.value })} />
                      </td>
                    </tr>
                    {l.marcado && (falta || l.caixa || ehComputador) ? (
                      <tr className="insumo-ck__sub">
                        <td />
                        <td colSpan={5}>
                          {falta ? (
                            <div className="insumo-ck__falta">
                              <strong>⚠️ ESTOQUE INSUFICIENTE</strong> — disponível {fmtQtd(falta.disponivel)},
                              {' '}solicitado {fmtQtd(falta.solicitado)}, déficit {fmtQtd(falta.deficit)}.
                            </div>
                          ) : null}
                          <div className="insumo-ck__extras">
                            {ehComputador ? (
                              <>
                                <label className="insumo-ck__extra">
                                  <span>Modelo</span>
                                  <input className="insumo-ck__texto" placeholder="Ex.: Lenovo M720q" value={l.modelo} maxLength={150}
                                    onChange={(e) => setLinha(m.id, { modelo: e.target.value })} />
                                </label>
                                <label className="insumo-ck__extra">
                                  <span>Data da retirada</span>
                                  <input className="insumo-ck__texto" type="date" value={l.data_retirada}
                                    onChange={(e) => setLinha(m.id, { data_retirada: e.target.value })} />
                                </label>
                              </>
                            ) : null}
                            {l.caixa ? (
                              <label className="insumo-ck__extra">
                                <span>Detalhe da caixa</span>
                                <input className="insumo-ck__texto" placeholder="Ex.: caixa com 100 unidades" value={l.caixa_detalhe} maxLength={255}
                                  onChange={(e) => setLinha(m.id, { caixa_detalhe: e.target.value })} />
                              </label>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="content-card">
        <h3 className="insumo-ck__secao">Ferramentas</h3>
        <p className="content-card__hint">
          Conferência do que vai na bolsa. Ferramentas não baixam do estoque. Para acrescentar, remover ou
          substituir uma ferramenta da lista (ex.: troca por desgaste), use a aba <strong>Ferramentas</strong>.
        </p>
        <div className="insumo-ck__ferramentas">
          {ferramentas.map((f) => (
            <label key={f.nome} className="insumo-ck__ferramenta">
              <input
                type="checkbox" checked={f.marcada}
                onChange={(e) => setFerramentas((fs) => fs.map((x) => (x.nome === f.nome ? { ...x, marcada: e.target.checked } : x)))}
              />
              {f.nome}
            </label>
          ))}
        </div>
        <div className="insumo-ck__nova-ferramenta">
          <input
            className="insumo-ck__texto" placeholder="Outros: nome da ferramenta" value={novaFerramenta} maxLength={100}
            onChange={(e) => setNovaFerramenta(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionarFerramenta(); } }}
          />
          <button type="button" className="btn-portal btn-portal--outline" onClick={adicionarFerramenta}>
            <i className="bi bi-plus-lg" /> Adicionar
          </button>
        </div>
      </section>

      <section className="content-card">
        <h3 className="insumo-ck__secao">Verificação</h3>
        <ul className="insumo-ck__verificacao">
          {faltas.length ? faltas.map((f) => (
            <li key={f.insumo_id} className="insumo-ck__alerta">
              <i className="bi bi-exclamation-triangle-fill" /> Estoque insuficiente — {f.nome}: disponível {fmtQtd(f.disponivel)},
              {' '}solicitado {fmtQtd(f.solicitado)}, déficit {fmtQtd(f.deficit)}.
            </li>
          )) : itensForm.length ? (
            <li className="insumo-ck__ok"><i className="bi bi-check-circle-fill" /> Estoque suficiente para os materiais selecionados.</li>
          ) : null}
          {pendenciasPreparo.map((p) => (
            <li key={p} className="insumo-ck__pendente"><i className="bi bi-dash-circle" /> {p}</li>
          ))}
          {ferramentasNaoMarcadas.length ? (
            <li className="cell-muted">
              <i className="bi bi-info-circle" /> Ferramentas não selecionadas: {ferramentasNaoMarcadas.map((f) => f.nome).join(', ')}.
            </li>
          ) : null}
          {dados.observacoes.trim() ? (
            <li className="cell-muted"><i className="bi bi-chat-left-text" /> {dados.observacoes}</li>
          ) : null}
        </ul>
      </section>

      <div className="portal-form-actions insumo-ck__acoes">
        {checklist ? (
          <button type="button" className="btn-portal btn-portal--outline" disabled={salvando} onClick={cancelar}>
            <i className="bi bi-x-circle" /> Cancelar checklist
          </button>
        ) : null}
        <button type="button" className="btn-portal btn-portal--outline" disabled={salvando} onClick={salvarRascunho}>
          <i className="bi bi-save" /> Salvar rascunho
        </button>
        <button
          type="button" className="btn-portal btn-portal--primary"
          disabled={salvando || !podeConferir} onClick={conferir}
          title={podeConferir ? '' : 'Resolva os itens da verificação'}
        >
          <i className="bi bi-clipboard-check" /> {salvando ? 'Salvando...' : 'Conferir checklist'}
        </button>
      </div>
    </div>
  );
}

// Materiais em ordem alfabetica com a linha OUTROS no lugar dela.
function linhasMateriais(materiais) {
  const lista = [...materiais].sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'));
  const idx = lista.findIndex((m) => m.descricao.localeCompare('OUTROS', 'pt-BR') > 0);
  lista.splice(idx === -1 ? lista.length : idx, 0, OUTROS);
  return lista;
}

// Retirado / usado / devolvido / consumo real de cada item. Usada tambem no
// historico da aba Insumos.
export function TabelaConsumo({ itens, mostrarEstoque = false }) {
  return (
    <div className="table-shell">
      <table className="portal-table">
        <thead>
          <tr>
            <th>Material</th><th>Retirado</th><th>Usado</th><th>Devolvido</th><th>Consumo real</th>
            {mostrarEstoque ? <th>Estoque atual</th> : null}
          </tr>
        </thead>
        <tbody>
          {itens.map((it) => (
            <tr key={it.id}>
              <td><span className="cell-strong">{it.nome}</span><DetalheItem it={it} /></td>
              <td>{fmtQtd(it.qtd_retirada)}</td>
              <td>{fmtQtd(it.qtd_usada)}</td>
              <td>{fmtQtd(it.qtd_devolvida)}</td>
              <td><strong>{fmtQtd(it.qtd_usada)}</strong></td>
              {mostrarEstoque ? (
                <td className="cell-muted">{it.insumo_id ? fmtQtd(it.estoque_atual) : 'sem estoque'}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
